/* MÂZIA shared helpers: window.MZ. Loaded with `defer` on every page by layout/theme.liquid.
   Deferred and module scripts run in document order, so any <script defer> or <script type="module">
   placed after it can use MZ directly. An inline (classic) script runs before MZ exists: queue the work
   with (window.mzQueue = window.mzQueue || []).push((MZ) => { ... }).
   Contract: specs/10-foundation-contract.md. */
(() => {
  const MZ = (window.MZ = window.MZ || {});
  const root = document.documentElement;
  const DAY_MS = 86400000;

  /* ---------- Settings printed by snippets/mz-settings-json.liquid ---------- */
  let settings = {};
  try {
    const node = document.getElementById('mz-settings');
    if (node) settings = JSON.parse(node.textContent) || {};
  } catch (error) {
    settings = {};
  }
  MZ.settings = settings;

  /* ---------- Event bus: document CustomEvents named "mz:<name>" ---------- */
  MZ.emit = (name, detail) => document.dispatchEvent(new CustomEvent(`mz:${name}`, { detail }));
  MZ.on = (name, handler, options) => {
    const listener = (event) => handler(event.detail, event);
    document.addEventListener(`mz:${name}`, listener, options);
    return () => document.removeEventListener(`mz:${name}`, listener, options);
  };

  /* ---------- Breakpoints and motion ---------- */
  MZ.mq = {
    desktop: window.matchMedia('(min-width: 750px)'),
    wide: window.matchMedia('(min-width: 990px)'),
    hover: window.matchMedia('(hover: hover)'),
  };
  MZ.isDesktop = () => MZ.mq.desktop.matches;

  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  MZ.reducedMotion = motion.matches;
  motion.addEventListener?.('change', (event) => {
    MZ.reducedMotion = event.matches;
    MZ.emit('motion', { reduced: event.matches });
  });

  /* ---------- Money: paise in, "₹5,486" out (whole rupees, Indian grouping), same as snippets/mz-money ---------- */
  const shopCurrency = window.Shopify?.currency?.active || settings.currency || 'INR';
  const indian = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });
  MZ.money = (cents) => {
    const value = Number(cents) || 0;
    const rupees = Math.round(Math.abs(value) / 100);
    const sign = value < 0 && rupees > 0 ? '−' : '';
    if (shopCurrency !== 'INR') {
      try {
        return sign + new Intl.NumberFormat(undefined, { style: 'currency', currency: shopCurrency, maximumFractionDigits: 0 }).format(rupees);
      } catch (error) {
        /* fall through to rupees */
      }
    }
    return `${sign}₹${indian.format(rupees)}`;
  };
  /* For Horizon's formatMoney() (thousands grouping, not lakh): "₹{{amount_no_decimals}}" */
  MZ.moneyFormat = '₹{{amount_no_decimals}}';

  /* ---------- WhatsApp link ---------- */
  MZ.whatsappUrl = (message = settings.whatsappMessage, number = settings.whatsapp) => {
    const digits = String(number || '').replace(/\D/g, '');
    return `https://wa.me/${digits}${message ? `?text=${encodeURIComponent(message)}` : ''}`;
  };

  /* ---------- Dates in India time (IST) ---------- */
  const istParts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hourCycle: 'h23',
  });
  /* Today in IST. `date` is that calendar day as a UTC-midnight Date: add days with addDays(), read it with getUTC*(). */
  MZ.ist = (now = new Date()) => {
    const p = {};
    for (const { type, value } of istParts.formatToParts(now)) p[type] = Number(value);
    const date = new Date(Date.UTC(p.year, p.month - 1, p.day));
    return { year: p.year, month: p.month, day: p.day, hour: p.hour % 24, minute: p.minute, second: p.second, weekday: date.getUTCDay(), date };
  };
  MZ.addDays = (date, days) => new Date(date.getTime() + days * DAY_MS);

  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  /* "26 Oct" / "Mon 26 Oct" */
  MZ.formatDate = (date, { weekday = false } = {}) =>
    `${weekday ? `${WEEKDAYS[date.getUTCDay()]} ` : ''}${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]}`;
  /* "26 – 30 Oct", "28 Oct – 1 Nov", "Mon 19 – Wed 21 Oct"; spaced: false gives "17–24 Oct" */
  MZ.formatRange = (from, to, { weekday = false, spaced = true } = {}) => {
    if (from.getTime() === to.getTime()) return MZ.formatDate(from, { weekday });
    const dash = spaced ? ' – ' : '–';
    const sameMonth = from.getUTCMonth() === to.getUTCMonth() && from.getUTCFullYear() === to.getUTCFullYear();
    const left = sameMonth ? `${weekday ? `${WEEKDAYS[from.getUTCDay()]} ` : ''}${from.getUTCDate()}` : MZ.formatDate(from, { weekday });
    return left + dash + MZ.formatDate(to, { weekday });
  };
  /* Minutes left as "7h 21m" (rounded up to the minute, as the design does) */
  MZ.formatCountdown = (ms) => {
    const minutes = Math.max(1, Math.ceil(ms / 60000));
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return [h ? `${h}h` : '', m ? `${m}m` : ''].filter(Boolean).join(' ');
  };

  /* ---------- Delivery estimate: one rule for the product page, bag and policy copy ----------
     Working days are counted after the order day (days off in settings.delivery.blocked are skipped).
     Orders at or after the cutoff hour (IST) count from the next day.
     Processing ends between W(processMin) and W(processMax); delivery is transitMin to transitMax working
     days after the latest processing day. With the defaults this reproduces the Essential Estimated
     Delivery app on the live store (order 15 Oct: processing 26–27 Oct, delivered 2–6 Nov). */
  MZ.delivery = (now = new Date(), overrides = {}) => {
    const d = { processMin: 7, processMax: 8, transitMin: 4, transitMax: 8, days: 'mon_fri', cutoffHour: 24, blocked: [], ...(settings.delivery || {}), ...overrides };
    const blocked = new Set((d.blocked || []).map((entry) => String(entry).trim()).filter(Boolean));
    const isWorkingDay = (date) => {
      const weekday = date.getUTCDay();
      if (d.days === 'mon_fri' && (weekday === 0 || weekday === 6)) return false;
      if (d.days === 'mon_sat' && weekday === 0) return false;
      const monthDay = `${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
      return !blocked.has(monthDay);
    };
    const addWorkingDays = (date, count) => {
      let day = date;
      for (let counted = 0, guard = 0; counted < count && guard < 400; guard += 1) {
        day = MZ.addDays(day, 1);
        if (isWorkingDay(day)) counted += 1;
      }
      return day;
    };
    const today = MZ.ist(now);
    const cutoff = Number(d.cutoffHour);
    const minutesNow = today.hour * 60 + today.minute + today.second / 60;
    const cutoffPassed = cutoff < 24 && minutesNow >= cutoff * 60;
    const counted = cutoffPassed ? MZ.addDays(today.date, 1) : today.date;
    const dispatchFrom = addWorkingDays(counted, Number(d.processMin));
    const dispatchTo = addWorkingDays(counted, Number(d.processMax));
    return {
      today: today.date,
      cutoffPassed,
      msToCutoff: cutoffPassed ? 0 : Math.round((cutoff * 60 - minutesNow) * 60000),
      dispatchFrom,
      dispatchTo,
      deliverFrom: addWorkingDays(dispatchTo, Number(d.transitMin)),
      deliverTo: addWorkingDays(dispatchTo, Number(d.transitMax)),
      isWorkingDay,
      addWorkingDays,
    };
  };

  /* ---------- Diwali delivery promise (Theme settings → MÂZIA → Diwali delivery promise) ----------
     An order placed today (India time) is delivered between today + min and today + max calendar days,
     guaranteed; orders placed until 23:59 on the order-by day are delivered before Diwali. It runs while
     settings.diwali.enabled (true only when snippets/mz-diwali-state.liquid said so as Shopify rendered the page)
     and until 23:59 India time on its last day. Liquid writes the first words (snippets/mz-diwali-text.liquid,
     the same tokens and formats); MZ.diwaliSync() keeps them right in the browser: a page from Shopify's cache,
     one left open past midnight or past the order-by day, the time left. */
  const IST_OFFSET_MS = 330 * 60000;
  const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const escapeHtml = (text) =>
    String(text ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const dayOf = (text) => {
    const m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(String(text || '').trim());
    return m ? new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))) : null;
  };

  /* { active, beforeCutoff, today, from, to (UTC-midnight days, as MZ.ist), min, max, cutoff, diwali, last,
       msLeft (to the end of the order-by day), copy (the texts, printed only while it runs) } */
  MZ.diwali = (now = new Date()) => {
    const s = settings.diwali || {};
    const today = MZ.ist(now).date;
    const last = dayOf(s.end) || dayOf(s.date);
    const cutoff = dayOf(s.cutoff);
    const min = Math.max(0, Math.round(Number(s.min) || 0));
    const max = Math.max(min, Math.round(Number(s.max) || 0));
    const active = Boolean(s.enabled && last && today.getTime() <= last.getTime());
    const beforeCutoff = Boolean(active && cutoff && today.getTime() <= cutoff.getTime());
    const msLeft = beforeCutoff ? Math.max(0, cutoff.getTime() + DAY_MS - IST_OFFSET_MS - now.getTime()) : 0;
    return {
      active,
      beforeCutoff,
      today,
      from: MZ.addDays(today, min),
      to: MZ.addDays(today, max),
      min,
      max,
      cutoff,
      diwali: dayOf(s.date),
      last,
      msLeft,
      copy: s.copy || {},
    };
  };

  /* A promise text with its tokens filled (snippets/mz-diwali-text.liquid lists them). hl: HTML, with the dates
     in <span class="mz-diwali__hl"> */
  MZ.diwaliText = (template, d = MZ.diwali(), { hl = false } = {}) => {
    const date = (text) => (hl ? `<span class="mz-diwali__hl">${text}</span>` : text);
    const values = {
      min: d.min,
      max: d.max,
      from: date(MZ.formatDate(d.from, { weekday: true })),
      to: date(MZ.formatDate(d.to, { weekday: true })),
      range: date(MZ.formatRange(d.from, d.to, { weekday: true })),
      cutoff: d.cutoff ? MZ.formatDate(d.cutoff) : '',
      cutoff_full: d.cutoff ? `${d.cutoff.getUTCDate()} ${MONTHS_LONG[d.cutoff.getUTCMonth()]}` : '',
      diwali: d.diwali ? MZ.formatDate(d.diwali) : '',
      days: Math.floor(d.msLeft / DAY_MS),
      time: d.msLeft > 0 ? MZ.formatCountdown(d.msLeft) : '',
    };
    const source = hl ? escapeHtml(template) : String(template || '');
    return source.replace(/\[(\w+)\]/g, (match, key) => (key in values ? String(values[key]) : match));
  };

  /* "8 days left to order for Diwali", "1 day left…", "Last day: 7h 21m left…"; '' once the order-by day is over */
  MZ.diwaliLeft = (d = MZ.diwali()) => {
    if (!d.beforeCutoff) return '';
    const days = Math.floor(d.msLeft / DAY_MS);
    const template = days >= 2 ? d.copy.leftDays : days === 1 ? d.copy.leftDay : d.copy.leftTime;
    return MZ.diwaliText(template, d);
  };

  /* The page's promise parts: [data-mz-diwali] while it runs, [data-mz-diwali-before] until the order-by day ends,
     [data-mz-diwali-after] after it, [data-mz-diwali-off] (the delivery estimate it stands in for, printed hidden)
     once it is over; [data-mz-diwali-fill="<copy key>"] holds that text ("left": the time left), as HTML with
     data-mz-diwali-hl; [data-mz-diwali-skin] keeps the Diwali colours. A message of the announcement bar that
     ends leaves with its dot, before assets/mz-header.js (which runs after this) starts the rotation. */
  const dropDiwali = (element) => {
    if (element.matches('[data-mz-announce-msg]')) {
      const before = element.previousElementSibling;
      const after = element.nextElementSibling;
      if (before?.matches('.mz-announce__dot')) before.remove();
      else if (after?.matches('.mz-announce__dot')) after.remove();
      element.remove();
      return;
    }
    element.hidden = true;
  };
  MZ.diwaliSync = (scope = document) => {
    const d = MZ.diwali();
    if (!settings.diwali?.enabled) return d;
    scope.querySelectorAll('[data-mz-diwali-fill]').forEach((element) => {
      const key = element.dataset.mzDiwaliFill;
      if (key !== 'left' && !(key in d.copy)) return;
      const hl = element.hasAttribute('data-mz-diwali-hl');
      const text = key === 'left' ? MZ.diwaliLeft(d) : MZ.diwaliText(d.copy[key], d, { hl });
      if (!hl) {
        if (element.textContent !== text) element.textContent = text;
      } else if (element.mzDiwaliHtml !== text) {
        element.mzDiwaliHtml = text;
        element.innerHTML = text;
      }
    });
    scope.querySelectorAll('[data-mz-diwali-after]').forEach((element) => {
      if (element.hidden !== d.beforeCutoff) element.hidden = d.beforeCutoff;
    });
    if (!d.beforeCutoff) scope.querySelectorAll('[data-mz-diwali-before]').forEach(dropDiwali);
    if (!d.active) {
      scope.querySelectorAll('[data-mz-diwali]').forEach(dropDiwali);
      scope.querySelectorAll('[data-mz-diwali-skin]').forEach((element) => element.removeAttribute('data-mz-diwali-skin'));
      scope.querySelectorAll('[data-mz-diwali-off][hidden]').forEach((element) => {
        element.hidden = false;
      });
    }
    return d;
  };
  if (settings.diwali?.enabled) {
    setInterval(() => {
      if (!document.hidden) MZ.diwaliSync();
    }, 30000);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) MZ.diwaliSync();
    });
  }

  /* ---------- Scroll lock for mz overlays (keyed, so nested sheets unlock correctly) ---------- */
  const locks = new Set();
  MZ.lockScroll = (key = 'default') => {
    locks.add(key);
    root.classList.add('mz-scroll-locked');
  };
  MZ.unlockScroll = (key = 'default') => {
    locks.delete(key);
    if (!locks.size) root.classList.remove('mz-scroll-locked');
  };

  /* ---------- Reveal on scroll: [data-mz-reveal] (value = visible fraction, default 0.15) gets .is-revealed once ---------- */
  const revealObservers = new Map();
  const revealObserver = (threshold) => {
    if (!revealObservers.has(threshold)) {
      revealObservers.set(
        threshold,
        new IntersectionObserver(
          (entries, observer) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              entry.target.classList.add('is-revealed');
              observer.unobserve(entry.target);
              MZ.emit('reveal', { element: entry.target });
            }
          },
          { threshold }
        )
      );
    }
    return revealObservers.get(threshold);
  };
  MZ.reveal = (scope = document) => {
    const elements = scope.querySelectorAll('[data-mz-reveal]:not(.is-revealed)');
    if (!elements.length) return;
    const showNow = MZ.reducedMotion || !('IntersectionObserver' in window) || window.Shopify?.designMode;
    for (const element of elements) {
      if (showNow) {
        element.classList.add('is-revealed');
        continue;
      }
      const threshold = Math.min(1, Math.max(0, parseFloat(element.dataset.mzReveal) || 0.15));
      revealObserver(threshold).observe(element);
    }
  };

  /* ---------- Drag to scroll a horizontal rail with the mouse: [data-mz-drag] ---------- */
  MZ.dragScroll = (rail) => {
    if (!rail || rail.mzDrag) return;
    rail.mzDrag = true;
    let pointer = null;
    let startX = 0;
    let startLeft = 0;
    let dragged = false;
    let swallowClick = false;
    rail.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      pointer = event.pointerId;
      startX = event.clientX;
      startLeft = rail.scrollLeft;
      dragged = false;
    });
    rail.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointer) return;
      const dx = event.clientX - startX;
      if (!dragged) {
        if (Math.abs(dx) < 6) return;
        dragged = true;
        rail.setPointerCapture?.(pointer);
        rail.classList.add('is-dragging');
      }
      rail.scrollLeft = startLeft - dx;
    });
    const end = (event) => {
      if (event.pointerId !== pointer) return;
      pointer = null;
      if (!dragged) return;
      rail.classList.remove('is-dragging');
      swallowClick = true;
      setTimeout(() => {
        swallowClick = false;
      }, 0);
    };
    rail.addEventListener('pointerup', end);
    rail.addEventListener('pointercancel', end);
    rail.addEventListener(
      'click',
      (event) => {
        if (!swallowClick) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
    rail.addEventListener('dragstart', (event) => event.preventDefault());
  };

  /* ---------- Keyboard focus in a sideways rail brings the whole item and its ring into view ----------
     Chrome leaves a focused element that is partly on screen where it is, e.g. a chip, a category tile or a card
     cut by the screen edge. Rails: pills (.mz-pills), card rails (.mz-rail, e.g. "You may also like"), the home
     categories and the bag's add-ons, while they scroll (phones; from 750px most are grids). The reels rail
     scrolls its own reels (assets/mz-reels.js). */
  const RAIL_ITEMS = '.mz-pills > *, .mz-rail:not(.mz-reels__row) > *, .mz-home-cats__list > *, .mz-bag-addons__list > *';
  const keyboardFocus = (element) => {
    try {
      return element.matches(':focus-visible');
    } catch (error) {
      return true;
    }
  };
  document.addEventListener('focusin', (event) => {
    const target = event.target;
    const item = target instanceof Element ? target.closest(RAIL_ITEMS) : null;
    if (!item || !keyboardFocus(target)) return;
    const rail = item.parentElement;
    if (rail.scrollWidth <= rail.clientWidth + 1) return;
    const box = item.getBoundingClientRect();
    const view = rail.getBoundingClientRect();
    const ring = 4; // the 2px focus ring 2px outside the item
    // Cut by an edge the rail can still scroll past (the first item of an edge-to-edge rail sits at the edge)
    const cutStart = box.left - ring < view.left && rail.scrollLeft > 0;
    const cutEnd = box.right + ring > view.right && rail.scrollLeft + rail.clientWidth < rail.scrollWidth - 1;
    if (!cutStart && !cutEnd) return;
    // The item lands as far from the edge as the rail's own padding (the page gutter, as at its ends), so the ring
    // stays whole; a rail that snaps puts it on its snap position
    const style = getComputedStyle(rail);
    const side = cutStart ? 'Left' : 'Right';
    const snaps = !['', 'none'].includes(style.scrollSnapType);
    const room = Math.max(ring, parseFloat(style[`padding${side}`]) || 0) - (parseFloat(style[`scrollPadding${side}`]) || 0);
    const margin = snaps ? 0 : Math.max(0, room);
    const before = item.style.scrollMarginInline;
    if (margin) item.style.scrollMarginInline = `${margin}px`;
    item.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    if (margin) item.style.scrollMarginInline = before;
  });

  /* ---------- Policy pages: the store's email addresses are links without an address ----------
     (Settings → Policies: Shipping and Refund). Give them their mailto: so a tap writes an email; without this
     script assets/mz-pages.css draws them as plain text. */
  document.querySelectorAll('.shopify-policy__body a:not([href])').forEach((link) => {
    const text = link.textContent.trim();
    if (/^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(text)) link.href = `mailto:${text}`;
  });

  /* ---------- 17TRACK order tracking (/apps/17TRACK: the app's own page, no template) ----------
     The app's Order Number / Tracking Number tabs are <div>s only a pointer can switch: they become a tablist
     (Tab reaches the chosen tab; Enter or Space picks the focused one, the arrow keys, Home and End move between
     them). Its fields get a phone's keyboard hints, as the theme's own forms have: the email field the email
     keyboard, no capital and no autocorrect; the order and tracking numbers no autocorrect (no inputmode: order
     names carry '#'). The app (React) draws the form after load, swaps classes on each switch and remounts the
     fields, so an observer keeps roles and hints in step; type and value stay the app's. Styles, with the
     tabs' focus ring: assets/mz-pages.css §16. */
  const trackPage = document.getElementById('MainContent');
  if (trackPage && (/^\/apps\/17track(\/|$)/i.test(location.pathname) || trackPage.querySelector('#yq-config, .yq-app'))) {
    const TAB = "[class*='ClassicFormTab_tab__']";
    const CHOSEN = /ClassicFormTab_active__/;
    const FIELD_HINTS = {
      orderForm_user_email: { inputmode: 'email', autocomplete: 'email', autocapitalize: 'off', autocorrect: 'off', spellcheck: 'false' },
      orderForm_order_no: { autocomplete: 'off', autocapitalize: 'off', autocorrect: 'off', spellcheck: 'false' },
      trackingForm_track_no: { autocomplete: 'off', autocapitalize: 'off', autocorrect: 'off', spellcheck: 'false' },
    };
    const setAttributes = (element, attributes) => {
      for (const [name, value] of Object.entries(attributes)) {
        if (element.getAttribute(name) !== value) element.setAttribute(name, value);
      }
    };
    const tabsOf = (tab) => [...tab.parentElement.children].filter((node) => node.matches(TAB));
    let queued = false;
    const sync = () => {
      queued = false;
      trackPage.querySelectorAll(TAB).forEach((tab) => {
        if (tab.parentElement) setAttributes(tab.parentElement, { role: 'tablist' });
        const chosen = CHOSEN.test(tab.className);
        setAttributes(tab, { role: 'tab', 'aria-selected': String(chosen), tabindex: chosen ? '0' : '-1' });
      });
      for (const [id, hints] of Object.entries(FIELD_HINTS)) {
        const field = document.getElementById(id);
        if (field) setAttributes(field, hints);
      }
    };
    // Only class and child changes: the attributes written here don't call it again
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(sync);
    }).observe(trackPage, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
    sync();

    const pick = (tab) => {
      tab.click();
      tab.focus();
      // The app may redraw the tabs after a switch: the focus goes back to the chosen tab if it fell off
      requestAnimationFrame(() => {
        const chosen = [...trackPage.querySelectorAll(TAB)].find((node) => CHOSEN.test(node.className));
        const active = document.activeElement;
        if (!chosen || active === chosen) return;
        if (!active || active === document.body || active.closest?.(TAB)) chosen.focus();
      });
    };
    trackPage.addEventListener('keydown', (event) => {
      const tab = event.target instanceof Element ? event.target.closest(TAB) : null;
      if (!tab || !tab.parentElement || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        pick(tab);
        return;
      }
      const tabs = tabsOf(tab);
      const index = tabs.indexOf(tab);
      const moves = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: tabs.length - 1 };
      if (!(event.key in moves) || index < 0) return;
      event.preventDefault();
      pick(tabs[(moves[event.key] + tabs.length) % tabs.length]);
    });
  }

  /* ---------- One open <details> at a time inside [data-mz-accordion-single] ---------- */
  MZ.accordionSingle = (container) => {
    if (!container || container.mzAccordion) return;
    container.mzAccordion = true;
    container.addEventListener(
      'toggle',
      (event) => {
        const opened = event.target;
        if (!(opened instanceof HTMLDetailsElement) || !opened.open) return;
        for (const other of container.querySelectorAll('details[open]')) {
          if (other !== opened && other.closest('[data-mz-accordion-single]') === container) other.open = false;
        }
      },
      true
    );
  };

  /* ---------- Viewport width without the scrollbar, for vw-based maths: --mz-vw ---------- */
  const setViewportWidth = () => root.style.setProperty('--mz-vw', `${root.clientWidth}px`);
  setViewportWidth();
  window.addEventListener('resize', setViewportWidth, { passive: true });

  /* ---------- Back/forward keeps the scroll position ----------
     Horizon's scroll-container.js switches native scroll restoration off from 990px because it scrolls
     .page-wrapper there; mz-theme.css makes the document scroll, so switch it back on. */
  const autoRestore = () => {
    try {
      history.scrollRestoration = 'auto';
    } catch (error) {
      /* not supported */
    }
  };
  autoRestore();
  window.addEventListener('load', autoRestore);
  window.addEventListener('pageshow', autoRestore);
  MZ.mq.wide.addEventListener?.('change', () => setTimeout(autoRestore, 0));

  /* ---------- Init (also for sections the theme editor re-renders) ---------- */
  MZ.init = (scope = document) => {
    MZ.diwaliSync(scope);
    MZ.reveal(scope);
    scope.querySelectorAll('[data-mz-drag]').forEach(MZ.dragScroll);
    scope.querySelectorAll('[data-mz-accordion-single]').forEach(MZ.accordionSingle);
  };
  MZ.init();
  document.addEventListener('shopify:section:load', (event) => MZ.init(event.target));

  /* Run work queued by inline scripts, then run new work immediately */
  const queue = Array.isArray(window.mzQueue) ? window.mzQueue : [];
  window.mzQueue = { push: (fn) => (typeof fn === 'function' ? fn(MZ) : undefined) };
  queue.forEach((fn) => window.mzQueue.push(fn));

  MZ.ready = true;
  MZ.emit('ready', MZ);
})();
